// apps/web/src/components/merch/MerchPerformancePanel.tsx
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { AsyncBoundary } from '@/components/AsyncBoundary'

interface PerformanceData {
  from: string
  to: string
  channel: string
  revenuePence: number
  orderCount: number
  units: number
  bySource: Record<string, { revenuePence: number; orderCount: number; units: number }>
  topProducts: Array<{ title: string; units: number; revenuePence: number }>
}

function formatMoney(pence: number): string {
  return `£${(pence / 100).toFixed(2)}`
}

export function MerchPerformancePanel() {
  const [preset, setPreset] = useState<'7d' | '1m' | '1y' | 'custom'>('7d')
  const [days, setDays] = useState(14)
  const [months, setMonths] = useState(3)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [channel, setChannel] = useState<'all' | 'shopify' | 'woocommerce'>('all')
  const [mode, setMode] = useState<'preset' | 'days' | 'months' | 'range'>('preset')

  const params = new URLSearchParams({ channel })
  if (mode === 'preset') params.set('preset', preset === 'custom' ? '7d' : preset)
  if (mode === 'days') params.set('days', String(days))
  if (mode === 'months') params.set('months', String(months))
  if (mode === 'range' && from && to) {
    params.set('from', from)
    params.set('to', to)
  }

  const perfQ = useQuery<PerformanceData, ApiClientError>({
    queryKey: ['merch', 'store-performance', params.toString()],
    queryFn: () => artistFetch<PerformanceData>(`/merch/store/performance?${params}`),
  })

  return (
    <section className="rounded-xl border border-ovation-ink/10 bg-white p-6">
      <h2 className="font-display text-xl">Sales performance</h2>
      <p className="mt-1 text-sm text-ovation-muted">
        Revenue and units across Shopify and WooCommerce for a flexible date range.
      </p>

      <div className="mt-4 flex flex-wrap gap-3">
        <select
          value={mode}
          onChange={(e) => setMode(e.target.value as typeof mode)}
          className="rounded border border-ovation-ink/15 px-2 py-1.5 text-sm"
        >
          <option value="preset">Preset</option>
          <option value="days">Last X days</option>
          <option value="months">Last X months</option>
          <option value="range">Custom range</option>
        </select>

        {mode === 'preset' && (
          <select
            value={preset}
            onChange={(e) => setPreset(e.target.value as typeof preset)}
            className="rounded border border-ovation-ink/15 px-2 py-1.5 text-sm"
          >
            <option value="7d">7 days</option>
            <option value="1m">1 month</option>
            <option value="1y">1 year</option>
          </select>
        )}
        {mode === 'days' && (
          <input
            type="number"
            min={1}
            max={365}
            value={days}
            onChange={(e) => setDays(Number(e.target.value) || 1)}
            className="w-24 rounded border border-ovation-ink/15 px-2 py-1.5 text-sm"
          />
        )}
        {mode === 'months' && (
          <input
            type="number"
            min={1}
            max={36}
            value={months}
            onChange={(e) => setMonths(Number(e.target.value) || 1)}
            className="w-24 rounded border border-ovation-ink/15 px-2 py-1.5 text-sm"
          />
        )}
        {mode === 'range' && (
          <>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="rounded border border-ovation-ink/15 px-2 py-1.5 text-sm"
            />
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="rounded border border-ovation-ink/15 px-2 py-1.5 text-sm"
            />
          </>
        )}

        <select
          value={channel}
          onChange={(e) => setChannel(e.target.value as typeof channel)}
          className="rounded border border-ovation-ink/15 px-2 py-1.5 text-sm"
        >
          <option value="all">All channels</option>
          <option value="shopify">Shopify</option>
          <option value="woocommerce">WooCommerce</option>
        </select>
      </div>

      <AsyncBoundary isLoading={perfQ.isLoading} error={perfQ.error}>
        {perfQ.data && (
          <div className="mt-4 space-y-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <Stat label="Revenue" value={formatMoney(perfQ.data.revenuePence)} />
              <Stat label="Orders" value={String(perfQ.data.orderCount)} />
              <Stat label="Units" value={String(perfQ.data.units)} />
            </div>
            {Object.keys(perfQ.data.bySource).length > 0 && (
              <div className="flex flex-wrap gap-3 text-xs text-ovation-muted">
                {Object.entries(perfQ.data.bySource).map(([src, v]) => (
                  <span key={src}>
                    {src}: {v.orderCount} orders · {formatMoney(v.revenuePence)}
                  </span>
                ))}
              </div>
            )}
            {perfQ.data.topProducts.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ovation-muted">
                  Top products
                </p>
                <ul className="space-y-1 text-sm">
                  {perfQ.data.topProducts.slice(0, 8).map((p) => (
                    <li key={p.title} className="flex justify-between gap-4">
                      <span>{p.title}</span>
                      <span className="text-ovation-muted">
                        {p.units} · {formatMoney(p.revenuePence)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </AsyncBoundary>
    </section>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-ovation-ink/10 bg-ovation-ink/5 p-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-ovation-muted">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-ovation-ink">{value}</p>
    </div>
  )
}
